import type { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import {
  getStorefrontPublishStatus,
  type StorefrontProductKey,
  StorefrontPublishStatus,
} from "@redotech/redo-team-common/storefront-publish";
import { safeAssertNever } from "@redotech/util/type";
import { PreviewModeState } from "./preview-mode/preview-state";
import {
  getThemePublishedStatus,
  ThemePublishedStatus,
} from "./site-config/theme";

/**
 * Whether a storefront product is allowed to render given its publish status and
 * the current theme. Layers on top of each product's own enabled flag — callers
 * still check that the product is enabled.
 *
 * - preview mode → always render (the builder bypasses every gate)
 * - DISABLED → never render (kill switch)
 * - DRAFT_THEMES → render only on draft/unpublished (staging) themes
 * - LIVE_SITE → render everywhere
 *
 * An absent status defaults to LIVE_SITE so teams that have never configured
 * publish status keep rendering as they do today.
 */
export const passesStorefrontPublishGate = (
  autocheck: WidgetAutocheck,
  product: StorefrontProductKey,
): boolean => {
  if (PreviewModeState.isActive()) {
    return true;
  }
  const publishStatus = getStorefrontPublishStatus(
    autocheck.storefrontPublishStatus,
    product,
  );
  switch (publishStatus) {
    case StorefrontPublishStatus.DISABLED:
      return false;
    case StorefrontPublishStatus.DRAFT_THEMES:
      return getThemePublishedStatus() === ThemePublishedStatus.DRAFT;
    case StorefrontPublishStatus.LIVE_SITE:
      return true;
    default:
      return safeAssertNever(publishStatus);
  }
};
